'use client';

import ActionSheet from '@/components/ActionSheet';
import Dialog from '@/components/Dialog';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { getStoreInstance, getStoreState } from '@/store';
import logger from '@/utils/client-logger';
import { COMPLIANCE_SCENE_CODES } from '@/utils/constants';
import { env } from '@/utils/env';
import { NewCloseOutlined, WapNavBackOutlined } from '@zeelool/icon';
import type {
  PDCardCalibrationEventCode,
  PDCardCalibrationMeasurementResult,
} from '@zeelool/pd-card-calibration';
import CircularProgress from '@mui/material/CircularProgress';
import Image from 'next/image';
import Link from 'next/link';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatRecommendedFrameSizes,
  resolveFaceMeasurementResult,
  resolvePdMeasurementResult,
  toRawFaceMeasurementResult,
} from '../result';
import {
  createMeasurementTimeoutController,
  type MeasurementTimeoutController,
} from '../measurement-timeout.client';
import {
  createMeasurementSdkSession,
  DEFAULT_MEASUREMENT_SDK_AUDIO_CONFIG,
  resolveMeasurementSdkFailureReason,
  type MeasurementPromptVisual,
  type MeasurementSdkAudioConfig,
  type MeasurementSdkConfig,
  type MeasurementSdkFailureReason,
  type MeasurementSdkSession,
} from '../sdk-adapter.client';
import { MEASUREMENT_PREPARATION_VIDEO_URL, MEASUREMENT_SDK_EVENT_CODE } from '../sdk-contract';
import { useMeasurementPersistence } from '../persistence.client';
import {
  FACE_WIDTH_MEASUREMENT_REQUEST_EVENT,
  PD_MEASUREMENT_REQUEST_EVENT,
} from '../request.client';
import type {
  FaceMeasurementRecord,
  FaceWidthMeasurementDialogMessages,
  FaceWidthMeasurementRequest,
  MeasurementDialogScope,
  PdMeasurementRecord,
  PdMeasurementRequest,
} from '../types';
import {
  reportMeasurementButtonClick,
  reportMeasurementInteractionClick,
  reportMeasurementPageView,
} from '../tracking.client';
import { reportMeasurementComplianceConsent } from '../measurement-compliance-consent.client';
import { MEASUREMENT_TRACKING_STEP } from '../tracking';
import styles from './FaceWidthMeasurementDialogHost.module.css';
import MeasurementPromptIcon from './MeasurementPromptIcon';

type MeasurementRequest = FaceWidthMeasurementRequest | PdMeasurementRequest;

type MeasurementRequestHandlers = {
  onFaceWidthRequest: (request: FaceWidthMeasurementRequest) => void;
  onPdRequest: (request: PdMeasurementRequest) => void;
};

const measurementRequestHandlersByScope = new Map<
  MeasurementDialogScope,
  MeasurementRequestHandlers
>();

function dispatchFaceWidthMeasurementRequest(event: Event) {
  const request = (event as CustomEvent<FaceWidthMeasurementRequest>).detail;
  measurementRequestHandlersByScope.get(request.scope ?? 'site')?.onFaceWidthRequest(request);
}

function dispatchPdMeasurementRequest(event: Event) {
  const request = (event as CustomEvent<PdMeasurementRequest>).detail;
  measurementRequestHandlersByScope.get(request.scope ?? 'site')?.onPdRequest(request);
}

function subscribeMeasurementRequests(
  scope: MeasurementDialogScope,
  handlers: MeasurementRequestHandlers,
) {
  const shouldAddWindowListeners = measurementRequestHandlersByScope.size === 0;
  measurementRequestHandlersByScope.set(scope, handlers);

  if (shouldAddWindowListeners) {
    window.addEventListener(
      FACE_WIDTH_MEASUREMENT_REQUEST_EVENT,
      dispatchFaceWidthMeasurementRequest,
    );
    window.addEventListener(PD_MEASUREMENT_REQUEST_EVENT, dispatchPdMeasurementRequest);
  }

  return () => {
    measurementRequestHandlersByScope.delete(scope);
    if (measurementRequestHandlersByScope.size === 0) {
      window.removeEventListener(
        FACE_WIDTH_MEASUREMENT_REQUEST_EVENT,
        dispatchFaceWidthMeasurementRequest,
      );
      window.removeEventListener(PD_MEASUREMENT_REQUEST_EVENT, dispatchPdMeasurementRequest);
    }
  };
}

const EXAMPLE_INSTRUCTION_KEYS_BY_MODE = {
  'pd-input': ['center', 'headCentered', 'removeGlasses'],
  card: ['center', 'headCentered', 'removeGlasses', 'cardPosition'],
} as const satisfies Record<
  MeasurementSdkConfig['mode'],
  readonly ('center' | 'headCentered' | 'removeGlasses' | 'cardPosition')[]
>;

const CORRECT_EXAMPLE_IMAGE_BY_MODE = {
  'pd-input': '/zeelool-next/lens-measurement/correct-example-without-card.png',
  card: '/zeelool-next/lens-measurement/correct-example-with-card.png',
} as const satisfies Record<MeasurementSdkConfig['mode'], string>;

const SUPPORTING_EXAMPLES_BY_MODE = {
  'pd-input': [{ image: 'with-glasses.png', messageKey: 'glasses' }],
  card: [
    { image: 'card-position.png', messageKey: 'tiltedCard' },
    { image: 'with-glasses.png', messageKey: 'glasses' },
  ],
} as const satisfies Record<
  MeasurementSdkConfig['mode'],
  readonly { image: string; messageKey: 'tiltedCard' | 'glasses' }[]
>;

function formatResultRecommendedFrameSizes(sizes: string[], fallback: string) {
  const recommendation = formatRecommendedFrameSizes(sizes);
  return recommendation ? recommendation.split(',').join(', ') : fallback;
}

function stripPreparationStepPrefix(message: string) {
  return message.replace(/^\d+\.\s*/, '');
}

const FEEDBACK_ICON_BY_STATE = {
  dislike: {
    default: '/zeelool-next/lens-measurement/runtime/feedback-dislike-default.svg',
    selected: '/zeelool-next/lens-measurement/runtime/feedback-dislike-selected.svg',
  },
  like: {
    default: '/zeelool-next/lens-measurement/runtime/feedback-like-default.svg',
    selected: '/zeelool-next/lens-measurement/runtime/feedback-like-selected.svg',
  },
} as const;

type DialogState =
  | { step: 'closed' }
  | { step: 'method'; request: FaceWidthMeasurementRequest }
  | { step: 'pd-risk'; request: PdMeasurementRequest }
  | { step: 'pd-input'; request: FaceWidthMeasurementRequest }
  | { step: 'preparation'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'video'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'example'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'permission'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'camera'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | {
      step: 'result';
      request: MeasurementRequest;
      config: MeasurementSdkConfig;
      record: FaceMeasurementRecord | PdMeasurementRecord;
    }
  | {
      step: 'failure';
      request: MeasurementRequest;
      config: MeasurementSdkConfig;
      reason: MeasurementSdkFailureReason | 'invalid-result' | 'timeout' | 'replace-card';
    };

const SINGLE_PD_OPTIONS = Array.from({ length: 49 }, (_, index) => {
  const value = 54 + index * 0.5;
  return {
    label: Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1),
    value: value.toFixed(2),
  };
});
const DUAL_PD_OPTIONS = Array.from({ length: 33 }, (_, index) => {
  const value = 25 + index * 0.5;
  return {
    label: Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1),
    value: value.toFixed(2),
  };
});
const PD_PICKER_Z_INDEX = 1400;
const MEASUREMENT_CONSENT_PAGE = '在线测量';
const ONLINE_MEASUREMENT_TOOL_AGREEMENT_URL =
  'https://common.xmslol.com/zeelool/pd/Terms_for_Online_Measurement_Tools_20260831V1.1.pdf';
const FACE_WIDTH_CARD_VIDEO_COMPLETED_KEY = 'zeelool:face-width-card-guide-completed:v1';

type FaceWidthCardVideoStatus = 'first-play' | 'watched-play' | 'ended' | 'error';

function hasCompletedFaceWidthCardVideo() {
  try {
    return window.localStorage.getItem(FACE_WIDTH_CARD_VIDEO_COMPLETED_KEY) === '1';
  } catch {
    return false;
  }
}

function markFaceWidthCardVideoCompleted() {
  try {
    window.localStorage.setItem(FACE_WIDTH_CARD_VIDEO_COMPLETED_KEY, '1');
  } catch {
    // The current session still unlocks measurement when browser storage is unavailable.
  }
}

async function getCameraPermissionState(): Promise<PermissionState | 'unknown'> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown';

  try {
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return status.state;
  } catch {
    return 'unknown';
  }
}

function getOpenStateConfig(state: DialogState) {
  return 'config' in state ? state.config : undefined;
}

function getOpenStateRequest(state: DialogState) {
  return 'request' in state ? state.request : undefined;
}

function setMeasurementDialogOpen(open: boolean) {
  getStoreInstance()?.getState().setMeasurementDialogOpen(open);
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <span className={styles.speakerIconFrame} aria-hidden>
      <Image
        src={`/zeelool-next/lens-measurement/runtime/volume-${muted ? 'off' : 'on'}.svg`}
        alt=""
        width={24}
        height={24}
        className={styles.speakerIconGlyph}
      />
    </span>
  );
}

function PreparationSpeakerIcon({ muted }: { muted: boolean }) {
  return <SpeakerIcon muted={muted} />;
}

function CameraCloseIcon() {
  return (
    <span className={styles.closeIconFrame} aria-hidden>
      <Image
        src="/zeelool-next/lens-measurement/runtime/close.svg"
        alt=""
        width={15}
        height={15}
        className={styles.closeIconGlyph}
      />
    </span>
  );
}

function PermissionCameraIcon({ status }: { status: 'on' | 'off' }) {
  const dimensions =
    status === 'off' ? { width: 50, height: 44.6668 } : { width: 51.3339, height: 36.6667 };

  return (
    <span className={styles.permissionCameraIcon} aria-hidden>
      <Image
        src={`/zeelool-next/lens-measurement/runtime/camera-${status}.svg`}
        alt=""
        width={dimensions.width}
        height={dimensions.height}
      />
    </span>
  );
}

const PREPARATION_GUIDE_ICON = {
  card: {
    src: '/zeelool-next/lens-measurement/preparation-card.svg',
    width: 20.2,
    height: 14.8,
    className: styles.preparationCardGlyph,
  },
  lighting: {
    src: '/zeelool-next/lens-measurement/sun.svg',
    width: 23.0461,
    height: 23.0461,
    className: styles.preparationSunGlyph,
  },
  volume: {
    src: '/zeelool-next/lens-measurement/volume.svg',
    width: 17,
    height: 15,
    className: styles.preparationVolumeGlyph,
  },
} as const;

const FACE_WIDTH_CARD_PREPARATION_ICON = {
  card: '/zeelool-next/lens-measurement/face-width-card-credit-card.svg',
  lighting: '/zeelool-next/lens-measurement/face-width-card-lighting.svg',
  volume: '/zeelool-next/lens-measurement/face-width-card-volume.svg',
} as const;

type PreparationChecklistItem = {
  icon: keyof typeof PREPARATION_GUIDE_ICON;
  text: string;
};

function PreparationGuideIcon({ kind }: { kind: keyof typeof PREPARATION_GUIDE_ICON }) {
  const icon = PREPARATION_GUIDE_ICON[kind];

  return (
    <span className={styles.preparationIconGlyphFrame} aria-hidden>
      <Image
        src={icon.src}
        alt=""
        width={icon.width}
        height={icon.height}
        className={icon.className}
      />
    </span>
  );
}

function FaceWidthCardPreparationIcon({
  kind,
}: {
  kind: keyof typeof FACE_WIDTH_CARD_PREPARATION_ICON;
}) {
  return (
    <Image
      src={FACE_WIDTH_CARD_PREPARATION_ICON[kind]}
      alt=""
      width={40}
      height={40}
      className={styles.faceWidthCardPreparationIcon}
      aria-hidden
    />
  );
}

function CardFailureIcon() {
  return (
    <span className={styles.cardFailureIcon} aria-hidden>
      <Image
        src="/zeelool-next/lens-measurement/runtime/card-failure.svg"
        alt=""
        width={53.1997}
        height={38.7999}
        className={styles.cardFailureIconGlyph}
      />
    </span>
  );
}

function MeasurementFailureIcon() {
  return (
    <span className={styles.measurementFailureIcon} aria-hidden>
      <Image
        src="/zeelool-next/lens-measurement/runtime/measurement-failure.svg"
        alt=""
        width={50}
        height={50}
        className={styles.measurementFailureIconGlyph}
      />
    </span>
  );
}

function HeaderControls({
  backLabel,
  closeLabel,
  onBack,
  onClose,
}: {
  backLabel: string;
  closeLabel: string;
  onBack?: () => void;
  onClose: () => void;
}) {
  return (
    <header className={styles.headerControls}>
      {onBack ? (
        <button type="button" className={styles.iconButton} aria-label={backLabel} onClick={onBack}>
          <WapNavBackOutlined aria-hidden className={styles.controlIcon} />
        </button>
      ) : (
        <span />
      )}
      <button type="button" className={styles.iconButton} aria-label={closeLabel} onClick={onClose}>
        <NewCloseOutlined aria-hidden className={styles.controlIcon} />
      </button>
    </header>
  );
}

function PreparationControls({
  backLabel,
  closeLabel,
  muteLabel,
  unmuteLabel,
  isMuted,
  onBack,
  onClose,
  onToggleMuted,
}: {
  backLabel: string;
  closeLabel: string;
  muteLabel: string;
  unmuteLabel: string;
  isMuted: boolean;
  onBack: () => void;
  onClose: () => void;
  onToggleMuted: () => void;
}) {
  return (
    <>
      <div className={styles.preparationControls}>
        <button type="button" aria-label={backLabel} onClick={onBack}>
          <WapNavBackOutlined aria-hidden />
        </button>
        <button type="button" aria-label={closeLabel} onClick={onClose}>
          <NewCloseOutlined aria-hidden />
        </button>
      </div>
      <button
        type="button"
        className={styles.preparationSoundControl}
        aria-label={isMuted ? unmuteLabel : muteLabel}
        onClick={onToggleMuted}
      >
        <PreparationSpeakerIcon muted={isMuted} />
      </button>
    </>
  );
}

function playPreparationVideo(video: HTMLVideoElement) {
  void video.play().catch((error: unknown) => {
    logger.warn('[FaceMeasurement] Failed to play preparation video', { error });
  });
}

function PreparationVideo({ muted, videoUrl }: { muted: boolean; videoUrl: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canPlay, setCanPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!canPlay || !shouldPlay || !video) return;

    playPreparationVideo(video);
  }, [canPlay, muted, shouldPlay]);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncReducedMotionPreference = () => {
      const nextShouldPlay = !reducedMotion.matches;
      setShouldPlay(nextShouldPlay);

      if (!nextShouldPlay) {
        setIsPlaying(false);
        videoRef.current?.pause();
      }
    };

    syncReducedMotionPreference();
    reducedMotion.addEventListener('change', syncReducedMotionPreference);
    return () => reducedMotion.removeEventListener('change', syncReducedMotionPreference);
  }, []);

  return (
    <>
      <Image
        src="/zeelool-next/lens-measurement/preparation-guide.png"
        alt=""
        fill
        sizes="100vw"
        priority
        className={`${styles.preparationPoster} ${isPlaying ? styles.preparationPosterHidden : ''}`}
      />
      {/* 视频仅作视觉演示，完整步骤说明由下方可访问文本提供。 */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={videoUrl}
        poster="/zeelool-next/lens-measurement/measurement-guide-poster.jpg"
        className={`${styles.preparationVideo} ${isPlaying ? styles.preparationVideoPlaying : ''}`}
        data-preparation-video
        data-ready={isPlaying}
        autoPlay
        loop
        muted={muted}
        playsInline
        preload="auto"
        aria-hidden="true"
        onCanPlay={() => setCanPlay(true)}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsPlaying(false)}
        onError={(event) => {
          setCanPlay(false);
          setIsPlaying(false);
          setShouldPlay(false);
          logger.warn('[FaceMeasurement] Failed to load preparation video', {
            error: event.currentTarget.error,
          });
        }}
      />
    </>
  );
}

function FaceWidthCardVideoGuide({
  backLabel,
  closeLabel,
  muteLabel,
  unmuteLabel,
  replayLabel,
  startLabel,
  videoTitle,
  videoInstructions,
  videoCompleted,
  videoUrl,
  isMuted,
  isSdkReady,
  isStartingCamera,
  hasCompletedInSession,
  onBack,
  onClose,
  onComplete,
  onStart,
  onToggleMuted,
}: {
  backLabel: string;
  closeLabel: string;
  muteLabel: string;
  unmuteLabel: string;
  replayLabel: string;
  startLabel: string;
  videoTitle: string;
  videoInstructions: string;
  videoCompleted: string;
  videoUrl: string;
  isMuted: boolean;
  isSdkReady: boolean;
  isStartingCamera: boolean;
  hasCompletedInSession: boolean;
  onBack: () => void;
  onClose: () => void;
  onComplete: () => void;
  onStart: () => void;
  onToggleMuted: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoTitleRef = useRef<HTMLHeadingElement>(null);
  const replayButtonRef = useRef<HTMLButtonElement>(null);
  const soundControlRef = useRef<HTMLButtonElement>(null);
  const [status, setStatus] = useState<FaceWidthCardVideoStatus>(() =>
    hasCompletedInSession || hasCompletedFaceWidthCardVideo() ? 'watched-play' : 'first-play',
  );
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
  const hasCompletedThisMountRef = useRef(status === 'watched-play');
  const [replayFocusRequest, setReplayFocusRequest] = useState(0);
  const showStart = status === 'watched-play' || status === 'ended';
  const hasEnded = status === 'ended';
  const showReplay = hasEnded || status === 'error';

  useEffect(() => {
    videoTitleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (hasEnded) replayButtonRef.current?.focus();
  }, [hasEnded]);

  useEffect(() => {
    if (replayFocusRequest > 0) soundControlRef.current?.focus();
  }, [replayFocusRequest]);

  const replayVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    setHasStartedPlayback(false);
    setStatus(
      hasCompletedThisMountRef.current || hasCompletedInSession || hasCompletedFaceWidthCardVideo()
        ? 'watched-play'
        : 'first-play',
    );
    setReplayFocusRequest((request) => request + 1);
    video.load();
    playPreparationVideo(video);
  };

  const toggleSoundAndRetryPlayback = () => {
    const video = videoRef.current;
    if (video) video.muted = !isMuted;
    onToggleMuted();
    if (video?.paused) playPreparationVideo(video);
  };

  return (
    <section className={styles.videoGuideScreen} aria-labelledby="measurement-video-title">
      <h1 ref={videoTitleRef} id="measurement-video-title" className="sr-only" tabIndex={-1}>
        {videoTitle}
      </h1>
      <p className="sr-only" aria-live="polite">
        {showStart ? videoCompleted : videoInstructions}
      </p>
      {/* 视频仅作视觉演示，完整测量步骤由后续摄像头引导提供。 */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={videoUrl}
        poster="/zeelool-next/lens-measurement/measurement-guide-poster.jpg"
        className={styles.videoGuideMedia}
        data-preparation-video
        autoPlay
        muted={isMuted}
        playsInline
        preload="auto"
        aria-hidden="true"
        onCanPlay={(event) => playPreparationVideo(event.currentTarget)}
        onPlaying={() => setHasStartedPlayback(true)}
        onEnded={() => {
          hasCompletedThisMountRef.current = true;
          onComplete();
          markFaceWidthCardVideoCompleted();
          setStatus('ended');
        }}
        onError={(event) => {
          logger.warn('[FaceMeasurement] Failed to load face-width guide video', {
            error: event.currentTarget.error,
          });
          if (
            !hasCompletedThisMountRef.current &&
            !hasCompletedInSession &&
            !hasCompletedFaceWidthCardVideo()
          ) {
            setStatus('error');
          }
        }}
      />

      <Image
        src="/zeelool-next/lens-measurement/measurement-guide-poster.jpg"
        alt=""
        fill
        sizes="100vw"
        className={`${styles.videoGuidePoster} ${
          hasStartedPlayback ? styles.videoGuidePosterHidden : ''
        }`}
        data-video-guide-poster
        aria-hidden
      />

      {showReplay ? <span className={styles.videoGuideEndedScrim} aria-hidden /> : null}

      <div className={styles.videoGuideControls}>
        <button type="button" aria-label={backLabel} onClick={onBack}>
          <WapNavBackOutlined aria-hidden />
        </button>
        <button type="button" aria-label={closeLabel} onClick={onClose}>
          <NewCloseOutlined aria-hidden />
        </button>
      </div>

      {!hasEnded ? (
        <button
          ref={soundControlRef}
          type="button"
          className={styles.videoGuideSoundControl}
          aria-label={isMuted ? unmuteLabel : muteLabel}
          onClick={toggleSoundAndRetryPlayback}
        >
          <PreparationSpeakerIcon muted={isMuted} />
        </button>
      ) : null}

      {showReplay ? (
        <button
          ref={replayButtonRef}
          type="button"
          className={styles.videoGuideReplay}
          onClick={replayVideo}
        >
          <Image
            src="/zeelool-next/lens-measurement/replay.svg"
            alt=""
            width={54}
            height={54}
            aria-hidden
          />
          <span>{replayLabel}</span>
        </button>
      ) : null}

      {showStart ? (
        <button
          type="button"
          className={`${styles.videoGuideStartButton} ${
            hasEnded ? styles.videoGuideStartButtonEnded : ''
          }`}
          aria-busy={!isSdkReady || isStartingCamera || undefined}
          disabled={!isSdkReady || isStartingCamera}
          onClick={onStart}
        >
          {!isSdkReady || isStartingCamera ? (
            <CircularProgress aria-hidden color="inherit" size={16} thickness={5} />
          ) : null}
          {startLabel}
        </button>
      ) : null}
    </section>
  );
}

function openLegalLink(event: MouseEvent<HTMLAnchorElement>, path: string) {
  event.preventDefault();
  window.open(path, '_blank');
}

function LegalNotice({
  messages,
  className,
}: {
  messages: FaceWidthMeasurementDialogMessages['method'];
  className?: string;
}) {
  return (
    <p className={`${styles.legalText} ${className || ''}`}>
      {messages.legalPrefix}
      <Link
        prefetch={false}
        href="/terms-conditions"
        onClick={(event) => openLegalLink(event, '/terms-conditions')}
      >
        {messages.terms}
      </Link>
      {messages.legalSeparator}
      <Link
        prefetch={false}
        href="/privacy-security"
        onClick={(event) => openLegalLink(event, '/privacy-security')}
      >
        {messages.privacy}
      </Link>
      {messages.legalConnector}
      <a
        className={styles.pendingLegalLink}
        href={ONLINE_MEASUREMENT_TOOL_AGREEMENT_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {messages.onlineAgreement}
      </a>
      {messages.legalSuffix ?? '.'}
    </p>
  );
}

function PrimaryButton({
  children,
  disabled,
  loading = false,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.primaryButton}
      aria-busy={loading || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {loading ? <CircularProgress aria-hidden color="inherit" size={16} thickness={5} /> : null}
      {children}
    </button>
  );
}

function reportMeasurementPreparationConsent(
  request: MeasurementRequest,
  config: MeasurementSdkConfig,
) {
  const token = getStoreState().token;
  if (!token) return;

  void reportMeasurementComplianceConsent({
    sceneCode: COMPLIANCE_SCENE_CODES.PD,
    page: MEASUREMENT_CONSENT_PAGE,
    metadata: {
      measurement_kind: request.kind,
      measurement_source: request.source,
      measurement_mode: config.mode,
    },
  }).catch((error) => {
    logger.warn('[FaceMeasurement] Failed to update measurement compliance consent', { error });
  });
}

function OptionSelect({
  id,
  label,
  placeholder,
  centerValue,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  centerValue: string;
  options: Array<{ label: string; value: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const displayValue = options.find((option) => option.value === value)?.label;

  return (
    <div className={styles.pdField}>
      <button
        id={id}
        type="button"
        className={styles.pdFieldButton}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-empty={value ? undefined : ''}
        onClick={() => setOpen(true)}
      >
        {value ? <span className={styles.pdFieldLabel}>{label}</span> : null}
        <span className={styles.pdFieldValue}>{displayValue || placeholder}</span>
      </button>
      <ActionSheet
        open={open}
        title={label}
        titleClassName="notranslate"
        options={options}
        defaultValue={centerValue}
        value={value}
        zIndex={PD_PICKER_Z_INDEX}
        onSave={(nextValue) => onChange(String(nextValue))}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

export default function FaceWidthMeasurementDialogHost({
  messages,
  scope = 'site',
  audioConfig = DEFAULT_MEASUREMENT_SDK_AUDIO_CONFIG,
  preparationVideoUrl = MEASUREMENT_PREPARATION_VIDEO_URL,
  measurementTimeoutMs = 20_000,
  sessionFactory = createMeasurementSdkSession,
}: {
  messages: FaceWidthMeasurementDialogMessages;
  scope?: MeasurementDialogScope;
  audioConfig?: MeasurementSdkAudioConfig;
  preparationVideoUrl?: string;
  measurementTimeoutMs?: number;
  sessionFactory?: typeof createMeasurementSdkSession;
}) {
  const [dialogState, setDialogState] = useState<DialogState>({ step: 'closed' });
  const [isSdkReady, setIsSdkReady] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [promptCode, setPromptCode] = useState<PDCardCalibrationEventCode>();
  const [promptVisual, setPromptVisual] = useState<MeasurementPromptVisual>();
  const [measurementProgress, setMeasurementProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isDualPd, setIsDualPd] = useState(false);
  const [singlePd, setSinglePd] = useState('');
  const [rightPd, setRightPd] = useState('');
  const [leftPd, setLeftPd] = useState('');
  const [pdValidationMessage, setPdValidationMessage] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<'like' | 'dislike'>();
  const [isSavingResult, setIsSavingResult] = useState(false);
  const [sdkContainer, setSdkContainer] = useState<HTMLElement | null>(null);
  const measurementPersistence = useMeasurementPersistence();
  const { trackEvent } = useReporting();
  const sdkSessionRef = useRef<MeasurementSdkSession | null>(null);
  const timeoutControllerRef = useRef<MeasurementTimeoutController | null>(null);
  const activeMeasurementRef = useRef<{
    request: MeasurementRequest;
    config: MeasurementSdkConfig;
  } | null>(null);
  const invalidResultAttemptsRef = useRef(0);
  const isMutedRef = useRef(false);
  const hasCompletedCardVideoInSessionRef = useRef(false);
  const isAwaitingRetryRef = useRef(false);
  const startRunIdRef = useRef(0);
  const promptCodeRef = useRef<PDCardCalibrationEventCode | undefined>(undefined);
  const measurementProgressRef = useRef(0);

  const disposeSdk = useCallback(() => {
    startRunIdRef.current += 1;
    timeoutControllerRef.current?.reset();
    sdkSessionRef.current?.dispose();
    sdkSessionRef.current = null;
    activeMeasurementRef.current = null;
    isAwaitingRetryRef.current = false;
    setIsSdkReady(false);
    setIsStartingCamera(false);
    setPromptCode(undefined);
    setPromptVisual(undefined);
    promptCodeRef.current = undefined;
    measurementProgressRef.current = 0;
    setMeasurementProgress(0);
  }, []);

  const closeDialog = useCallback(() => {
    disposeSdk();
    setMeasurementDialogOpen(false);
    setDialogState({ step: 'closed' });
    setFeedback(undefined);
    setIsSavingResult(false);
  }, [disposeSdk]);

  useEffect(() => {
    return subscribeMeasurementRequests(scope, {
      onFaceWidthRequest: (request) => {
        disposeSdk();
        setMeasurementDialogOpen(true);
        invalidResultAttemptsRef.current = 0;
        setDialogState({ step: 'method', request });
      },
      onPdRequest: (request) => {
        disposeSdk();
        setMeasurementDialogOpen(true);
        invalidResultAttemptsRef.current = 0;
        setDialogState(
          request.prescriptionRisk === 'requires-confirmation'
            ? { step: 'pd-risk', request }
            : { step: 'preparation', request, config: { mode: 'card' } },
        );
      },
    });
  }, [disposeSdk, scope]);

  useEffect(() => {
    window.addEventListener('pagehide', closeDialog);
    return () => window.removeEventListener('pagehide', closeDialog);
  }, [closeDialog]);

  useEffect(() => {
    return () => setMeasurementDialogOpen(false);
  }, []);

  useEffect(() => {
    const controller = createMeasurementTimeoutController({
      timeoutMs: measurementTimeoutMs,
      onTimeout: (step) => {
        const activeMeasurement = activeMeasurementRef.current;
        const session = sdkSessionRef.current;
        if (!activeMeasurement || !session) return;
        timeoutControllerRef.current?.reset();
        isAwaitingRetryRef.current = true;
        session.stopGuidance();
        setPromptCode(undefined);
        setPromptVisual(undefined);
        setDialogState({
          step: 'failure',
          request: activeMeasurement.request,
          config: activeMeasurement.config,
          reason: step === 'center-face' ? 'timeout' : 'replace-card',
        });
      },
    });
    const handleVisibilityChange = () => {
      controller.setVisible(document.visibilityState === 'visible');
    };

    timeoutControllerRef.current = controller;
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      controller.dispose();
      timeoutControllerRef.current = null;
    };
  }, [measurementTimeoutMs]);

  const sdkConfig = getOpenStateConfig(dialogState);
  const currentRequest = getOpenStateRequest(dialogState);
  const sdkMode = sdkConfig?.mode;
  const sdkPd = sdkConfig?.binocularPdMm;
  const entrySource = currentRequest?.source;
  const requestKind = currentRequest?.kind;
  const trackingStep =
    dialogState.step === 'preparation'
      ? MEASUREMENT_TRACKING_STEP.preparation
      : dialogState.step === 'video'
        ? MEASUREMENT_TRACKING_STEP.example
        : dialogState.step === 'example'
          ? MEASUREMENT_TRACKING_STEP.example
          : dialogState.step === 'permission'
            ? MEASUREMENT_TRACKING_STEP.permission
            : dialogState.step === 'camera'
              ? MEASUREMENT_TRACKING_STEP.camera
              : dialogState.step === 'result'
                ? MEASUREMENT_TRACKING_STEP.result
                : undefined;

  useEffect(() => {
    if (!requestKind || !entrySource || !sdkMode || !trackingStep) return;

    reportMeasurementPageView(trackEvent, {
      kind: requestKind,
      source: entrySource,
      mode: sdkMode,
      step: trackingStep,
    });
  }, [entrySource, requestKind, sdkMode, trackEvent, trackingStep]);

  useEffect(() => {
    if (!sdkMode || !entrySource || !sdkContainer || !currentRequest) return;

    let cancelled = false;
    const request = currentRequest;
    const config: MeasurementSdkConfig = {
      mode: sdkMode,
      ...(sdkPd === undefined ? {} : { binocularPdMm: sdkPd }),
    };

    disposeSdk();
    activeMeasurementRef.current = { request, config };
    invalidResultAttemptsRef.current = 0;

    const initialize = async () => {
      try {
        const session = await sessionFactory(
          sdkContainer,
          config,
          {
            onMessage: (code, visual) => {
              if (!cancelled && !isAwaitingRetryRef.current) {
                promptCodeRef.current = code;
                if (code !== MEASUREMENT_SDK_EVENT_CODE.Measuring) {
                  measurementProgressRef.current = 0;
                  setMeasurementProgress(0);
                }
                setPromptCode(code);
                setPromptVisual(visual);
                setIsStartingCamera(false);
                setDialogState((currentState) =>
                  currentState.step === 'permission'
                    ? { step: 'camera', request, config }
                    : currentState,
                );
              }
            },
            onProgress: (progress) => {
              if (
                cancelled ||
                isAwaitingRetryRef.current ||
                promptCodeRef.current !== MEASUREMENT_SDK_EVENT_CODE.Measuring ||
                !Number.isFinite(progress)
              ) {
                return;
              }
              const nextProgress = Math.round(Math.min(Math.max(progress, 0), 1) * 100);
              if (measurementProgressRef.current === nextProgress) return;
              measurementProgressRef.current = nextProgress;
              setMeasurementProgress(nextProgress);
            },
            onTimedStep: (step) => {
              if (!cancelled && !isAwaitingRetryRef.current) {
                timeoutControllerRef.current?.update(step);
              }
            },
            onResult: (sdkResult: PDCardCalibrationMeasurementResult) => {
              if (cancelled || isAwaitingRetryRef.current) return;
              timeoutControllerRef.current?.reset();
              const raw = toRawFaceMeasurementResult(sdkResult);
              const resolution =
                request.kind === 'pd'
                  ? resolvePdMeasurementResult({
                      raw,
                      entrySource: request.source,
                      sdkVersion: session.version,
                    })
                  : resolveFaceMeasurementResult({
                      raw,
                      entrySource: request.source,
                      sdkVersion: session.version,
                    });

              if (resolution.status === 'invalid') {
                if (invalidResultAttemptsRef.current === 0) {
                  invalidResultAttemptsRef.current = 1;
                  timeoutControllerRef.current?.reset();
                  setPromptCode(undefined);
                  setPromptVisual(undefined);
                  session.restart();
                  setDialogState({ step: 'camera', request, config });
                  return;
                }
                isAwaitingRetryRef.current = true;
                session.stopGuidance();
                setPromptCode(undefined);
                setPromptVisual(undefined);
                setDialogState({ step: 'failure', request, config, reason: 'invalid-result' });
                return;
              }

              session.stopGuidance();
              setFeedback(undefined);
              setDialogState({ step: 'result', request, config, record: resolution.record });
            },
          },
          audioConfig,
        );
        if (cancelled) {
          session.dispose();
          return;
        }
        sdkSessionRef.current = session;
        session.setMuted(isMutedRef.current);
        await session.init();
        if (!cancelled) setIsSdkReady(true);
      } catch (error) {
        if (cancelled) return;
        const reason = resolveMeasurementSdkFailureReason(error);
        if (reason === 'cancelled') return;
        disposeSdk();
        setDialogState({
          step: 'failure',
          request,
          config,
          reason,
        });
      }
    };

    void initialize();
    return () => {
      cancelled = true;
      disposeSdk();
    };
  }, [
    audioConfig,
    currentRequest,
    disposeSdk,
    entrySource,
    sdkContainer,
    sdkMode,
    sdkPd,
    sessionFactory,
  ]);

  const startCamera = async (request: MeasurementRequest, config: MeasurementSdkConfig) => {
    const session = sdkSessionRef.current;
    if (!session || !isSdkReady) return;
    const startRunId = startRunIdRef.current + 1;
    startRunIdRef.current = startRunId;
    const isCurrentStart = () =>
      startRunIdRef.current === startRunId && sdkSessionRef.current === session;

    isAwaitingRetryRef.current = false;
    timeoutControllerRef.current?.reset();
    setIsStartingCamera(true);
    setPromptCode(undefined);
    setPromptVisual(undefined);
    promptCodeRef.current = undefined;
    measurementProgressRef.current = 0;
    setMeasurementProgress(0);
    try {
      const permissionState = await getCameraPermissionState();
      if (!isCurrentStart()) return;
      if (permissionState !== 'granted') {
        setDialogState({ step: 'permission', request, config });
      }
      await session.start();
      if (!isCurrentStart()) return;
      setDialogState({ step: 'camera', request, config });
    } catch (error) {
      if (!isCurrentStart()) return;
      const reason = resolveMeasurementSdkFailureReason(error);
      if (reason === 'cancelled') {
        if (config.mode === 'card') {
          setDialogState({
            step: 'video',
            request,
            config,
          });
        } else if (request.kind === 'face-width') {
          setDialogState({ step: 'example', request, config });
        } else {
          setDialogState({ step: 'preparation', request, config });
        }
        return;
      }
      disposeSdk();
      setDialogState({
        step: 'failure',
        request,
        config,
        reason,
      });
    } finally {
      if (isCurrentStart()) setIsStartingCamera(false);
    }
  };

  const handlePdInputNext = (request: FaceWidthMeasurementRequest) => {
    const isMissingPd = isDualPd ? !rightPd || !leftPd : !singlePd;
    if (isMissingPd) {
      setPdValidationMessage(messages.pdInput.required);
      return;
    }

    const parsedSingle = Number(singlePd);
    const parsedRight = Number(rightPd);
    const parsedLeft = Number(leftPd);
    const pd = isDualPd ? parsedRight + parsedLeft : parsedSingle;
    if (!Number.isFinite(pd) || pd < 54 || pd > 78) {
      setPdValidationMessage(messages.pdInput.validation);
      return;
    }
    setPdValidationMessage(null);
    setDialogState({
      step: 'example',
      request,
      config: { mode: 'pd-input', binocularPdMm: pd },
    });
  };

  const selectFaceWidthMeasurementMethod = (
    request: FaceWidthMeasurementRequest,
    mode: MeasurementSdkConfig['mode'],
  ) => {
    reportMeasurementPageView(trackEvent, {
      kind: request.kind,
      source: request.source,
      mode,
      step: MEASUREMENT_TRACKING_STEP.method,
    });
    setDialogState(
      mode === 'card'
        ? { step: 'preparation', request, config: { mode } }
        : { step: 'pd-input', request },
    );
  };

  const handleBack = () => {
    if (dialogState.step === 'pd-input' || dialogState.step === 'preparation') {
      if (dialogState.request.kind === 'face-width') {
        disposeSdk();
        setDialogState({ step: 'method', request: dialogState.request });
      } else {
        closeDialog();
      }
      return;
    }
    if (dialogState.step === 'video') {
      setDialogState({
        step: 'preparation',
        request: dialogState.request,
        config: dialogState.config,
      });
      return;
    }
    if (dialogState.step === 'example') {
      if (dialogState.request.kind === 'face-width' && dialogState.config.mode === 'pd-input') {
        setDialogState({ step: 'pd-input', request: dialogState.request });
      } else {
        setDialogState({
          step: 'preparation',
          request: dialogState.request,
          config: dialogState.config,
        });
      }
    }
  };

  const continueFromPreparation = () => {
    if (dialogState.step !== 'preparation') return;

    reportMeasurementPreparationConsent(dialogState.request, dialogState.config);
    if (dialogState.config.mode === 'card') {
      setDialogState({
        step: 'video',
        request: dialogState.request,
        config: dialogState.config,
      });
      return;
    }

    setDialogState({
      step: 'example',
      request: dialogState.request,
      config: dialogState.config,
    });
  };

  const toggleMuted = () => {
    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    sdkSessionRef.current?.setMuted(nextMuted);
  };

  const restartMeasurement = (request: MeasurementRequest, config: MeasurementSdkConfig) => {
    const session = sdkSessionRef.current;
    if (!session) return;
    isAwaitingRetryRef.current = false;
    invalidResultAttemptsRef.current = 0;
    timeoutControllerRef.current?.reset();
    setIsStartingCamera(false);
    setPromptCode(undefined);
    setPromptVisual(undefined);
    promptCodeRef.current = undefined;
    measurementProgressRef.current = 0;
    setMeasurementProgress(0);
    setFeedback(undefined);
    session.restart();
    setDialogState({ step: 'camera', request, config });
  };

  const retryFromFailure = () => {
    if (
      dialogState.step !== 'failure' ||
      !['invalid-result', 'timeout', 'replace-card'].includes(dialogState.reason)
    ) {
      return;
    }
    restartMeasurement(dialogState.request, dialogState.config);
  };

  const refreshPage = () => {
    disposeSdk();
    window.location.reload();
  };

  const isOpen = dialogState.step !== 'closed';
  const showCamera = dialogState.step === 'camera' || dialogState.step === 'result';
  const isFinalMeasurement = promptCode === MEASUREMENT_SDK_EVENT_CODE.Measuring;
  const isPdInputPreparation =
    dialogState.step === 'preparation' && dialogState.config.mode === 'pd-input';
  const isFaceWidthCardPreparation =
    dialogState.step === 'preparation' &&
    dialogState.request.kind === 'face-width' &&
    dialogState.config.mode === 'card';
  const isPdCardPreparation =
    dialogState.step === 'preparation' &&
    dialogState.request.kind === 'pd' &&
    dialogState.config.mode === 'card';
  const usesCardPreparationLayout = isFaceWidthCardPreparation || isPdCardPreparation;
  const cardGuideVideoPreloadUrl =
    (dialogState.step === 'preparation' || dialogState.step === 'video') &&
    dialogState.config.mode === 'card'
      ? preparationVideoUrl
      : null;
  const preparationItems: PreparationChecklistItem[] =
    dialogState.step === 'preparation'
      ? isPdInputPreparation
        ? [
            { icon: 'lighting', text: messages.preparation.pdInputLighting },
            { icon: 'volume', text: messages.preparation.pdInputVolume },
          ]
        : [
            { icon: 'card', text: messages.preparation.card },
            { icon: 'lighting', text: messages.preparation.lighting },
            { icon: 'volume', text: messages.preparation.volume },
          ]
      : [];
  const prompt = promptCode ? messages.camera.prompts[promptCode] : messages.camera.starting;
  const resultPrimary =
    dialogState.step === 'result'
      ? 'onlinePdMm' in dialogState.record
        ? {
            label: messages.result.pdTitle,
            value: dialogState.record.onlinePdMm.toFixed(2),
          }
        : {
            label: messages.result.title,
            value: formatResultRecommendedFrameSizes(
              dialogState.record.recommendedFrameSizes,
              messages.result.recommendationUnavailable,
            ),
          }
      : null;
  const resultSubmitButtonName =
    dialogState.step === 'result'
      ? dialogState.request.kind === 'pd'
        ? messages.result.addPdToPrescription
        : messages.result.save
      : '';

  const remeasureFromResult = () => {
    if (dialogState.step !== 'result') return;

    reportMeasurementButtonClick(
      trackEvent,
      {
        kind: dialogState.request.kind,
        source: dialogState.request.source,
        mode: dialogState.config.mode,
        step: MEASUREMENT_TRACKING_STEP.result,
      },
      messages.result.remeasure,
    );
    if (feedback && !measurementPersistence.feedbackSubmitted) {
      reportMeasurementInteractionClick(
        trackEvent,
        {
          kind: dialogState.request.kind,
          source: dialogState.request.source,
          mode: dialogState.config.mode,
          step: MEASUREMENT_TRACKING_STEP.result,
        },
        feedback,
      );
      void measurementPersistence.saveFeedback(feedback);
    }
    restartMeasurement(dialogState.request, dialogState.config);
  };

  const saveResult = async () => {
    if (dialogState.step !== 'result' || isSavingResult) return;
    const trackingContext = {
      kind: dialogState.request.kind,
      source: dialogState.request.source,
      mode: dialogState.config.mode,
      step: MEASUREMENT_TRACKING_STEP.result,
    } as const;
    reportMeasurementButtonClick(trackEvent, trackingContext, resultSubmitButtonName);
    if (!measurementPersistence.feedbackSubmitted) {
      reportMeasurementInteractionClick(trackEvent, trackingContext, feedback);
    }
    setIsSavingResult(true);
    const feedbackPromise =
      feedback && !measurementPersistence.feedbackSubmitted
        ? measurementPersistence.saveFeedback(feedback)
        : Promise.resolve({ saved: true });
    const [measurementResult] = await Promise.all([
      measurementPersistence.saveMeasurement(dialogState.record),
      feedbackPromise,
    ]);
    if (!measurementResult.saved) {
      toast.error(measurementResult.message || messages.failure.unavailable);
      setIsSavingResult(false);
      return;
    }
    closeDialog();
  };
  const preparationMain =
    dialogState.step === 'preparation' ? (
      <main
        className={`${styles.screenBody} ${styles.preparationBody} ${
          isPdInputPreparation ? styles.preparationBodyWithoutMedia : ''
        } ${usesCardPreparationLayout ? styles.faceWidthCardPreparationBody : ''}`}
      >
        <h1>{messages.preparation.title}</h1>
        <ul
          className={`${styles.preparationList} ${
            usesCardPreparationLayout ? styles.faceWidthCardPreparationList : ''
          }`}
        >
          {preparationItems.map((item) => (
            <li key={item.icon}>
              <span className={styles.preparationIcon} aria-hidden>
                {usesCardPreparationLayout ? (
                  <FaceWidthCardPreparationIcon kind={item.icon} />
                ) : (
                  <PreparationGuideIcon kind={item.icon} />
                )}
              </span>
              <span>
                {usesCardPreparationLayout ? stripPreparationStepPrefix(item.text) : item.text}
              </span>
            </li>
          ))}
        </ul>
        {!isSdkReady ? (
          <p className="sr-only" aria-live="polite">
            {messages.preparation.loading}
          </p>
        ) : null}
        {dialogState.request.kind === 'pd' ? (
          <p
            className={`${styles.preparationRiskNotice} ${
              isPdCardPreparation ? styles.pdCardPreparationRiskNotice : ''
            }`}
          >
            {messages.preparation.riskNotice}
          </p>
        ) : null}
      </main>
    ) : null;

  useEffect(() => {
    if (!cardGuideVideoPreloadUrl) return;

    const videoPrefetch = document.createElement('link');
    videoPrefetch.rel = 'prefetch';
    videoPrefetch.as = 'video';
    videoPrefetch.type = 'video/mp4';
    videoPrefetch.href = cardGuideVideoPreloadUrl;
    videoPrefetch.dataset.measurementVideoPrefetch = '';
    document.head.append(videoPrefetch);

    return () => videoPrefetch.remove();
  }, [cardGuideVideoPreloadUrl]);

  return (
    <Dialog
      open={isOpen}
      fullScreen
      hideHeader
      footer={null}
      closeOnClickOverlay={false}
      aria-label={messages.ariaLabel}
      onClose={closeDialog}
      contentProps={{ className: styles.dialogContent }}
      slotProps={{ paper: { className: styles.dialogPaper } }}
    >
      <div className={styles.shell}>
        <section
          ref={setSdkContainer}
          className={`${styles.sdkContainer} ${showCamera ? styles.sdkContainerVisible : ''}`}
          aria-label={messages.camera.label}
        />

        {dialogState.step === 'method' || dialogState.step === 'pd-input' ? (
          <section className={styles.contentScreen}>
            <HeaderControls
              backLabel={messages.backLabel}
              closeLabel={messages.closeLabel}
              onClose={closeDialog}
            />
            <main
              className={`${styles.screenBody} ${styles.methodBody} ${
                dialogState.step === 'method'
                  ? styles.methodBodyCollapsed
                  : isDualPd
                    ? styles.methodBodyDual
                    : ''
              }`}
            >
              <h1>{messages.method.title}</h1>
              <div className={styles.methodList}>
                <button
                  type="button"
                  aria-label={messages.method.cardTitle}
                  className={styles.methodCard}
                  onClick={() => selectFaceWidthMeasurementMethod(dialogState.request, 'card')}
                >
                  <span className={styles.methodCardCopy}>
                    <strong>{messages.method.cardTitle}</strong>
                    <small>{messages.method.cardDescription}</small>
                  </span>
                </button>

                {dialogState.step === 'method' ? (
                  <button
                    type="button"
                    aria-label={`${messages.method.pdTitle} ${messages.method.pdQualifier}`}
                    className={styles.methodCard}
                    onClick={() =>
                      selectFaceWidthMeasurementMethod(dialogState.request, 'pd-input')
                    }
                  >
                    <span className={styles.methodCardCopy}>
                      <strong>
                        {messages.method.pdTitle}{' '}
                        <span className={styles.methodQualifier}>
                          {messages.method.pdQualifier}
                        </span>
                      </strong>
                      <small>{messages.method.pdDescription}</small>
                    </span>
                  </button>
                ) : (
                  <section className={styles.methodCardExpanded}>
                    <div className={styles.methodCardCopy}>
                      <strong>
                        {messages.method.pdTitle}{' '}
                        <span className={styles.methodQualifier}>
                          {messages.method.pdQualifier}
                        </span>
                      </strong>
                      <small>{messages.method.pdDescription}</small>
                    </div>
                    <div className={styles.pdControls}>
                      <div className={styles.pdFields}>
                        {isDualPd ? (
                          <>
                            <OptionSelect
                              id="measurement-right-pd"
                              label={messages.pdInput.rightPdLabel}
                              placeholder={messages.pdInput.rightPdLabel}
                              centerValue="32.00"
                              options={DUAL_PD_OPTIONS}
                              value={rightPd}
                              onChange={setRightPd}
                            />
                            <OptionSelect
                              id="measurement-left-pd"
                              label={messages.pdInput.leftPdLabel}
                              placeholder={messages.pdInput.leftPdLabel}
                              centerValue="32.00"
                              options={DUAL_PD_OPTIONS}
                              value={leftPd}
                              onChange={setLeftPd}
                            />
                          </>
                        ) : (
                          <OptionSelect
                            id="measurement-single-pd"
                            label={messages.pdInput.pdLabel}
                            placeholder={messages.pdInput.pdPlaceholder}
                            centerValue="63.00"
                            options={SINGLE_PD_OPTIONS}
                            value={singlePd}
                            onChange={setSinglePd}
                          />
                        )}
                      </div>
                      <label className={styles.checkboxLabel} htmlFor="measurement-dual-pd">
                        <input
                          id="measurement-dual-pd"
                          type="checkbox"
                          aria-label={messages.pdInput.singleLabel}
                          checked={isDualPd}
                          onChange={(event) => {
                            setIsDualPd(event.target.checked);
                            setPdValidationMessage(null);
                          }}
                        />
                        {messages.pdInput.singleLabel}
                      </label>
                      {pdValidationMessage ? (
                        <p role="alert" className={styles.validationMessage}>
                          {pdValidationMessage}
                        </p>
                      ) : null}
                      <PrimaryButton onClick={() => handlePdInputNext(dialogState.request)}>
                        {messages.pdInput.next}
                      </PrimaryButton>
                    </div>
                  </section>
                )}
              </div>
              <LegalNotice messages={messages.method} />
            </main>
          </section>
        ) : null}

        {dialogState.step === 'preparation' ? (
          <section
            className={`${styles.contentScreen} ${styles.preparationScreen} ${
              isPdInputPreparation ? styles.preparationScreenWithoutMedia : ''
            }`}
          >
            {isPdInputPreparation || usesCardPreparationLayout ? (
              <>
                <HeaderControls
                  backLabel={messages.backLabel}
                  closeLabel={messages.closeLabel}
                  onBack={handleBack}
                  onClose={closeDialog}
                />
                {preparationMain}
              </>
            ) : (
              <div className={styles.preparationScrollArea} data-preparation-scroll-area>
                <div className={styles.preparationMedia}>
                  <PreparationVideo muted={isMuted} videoUrl={preparationVideoUrl} />
                  <PreparationControls
                    backLabel={messages.backLabel}
                    closeLabel={messages.closeLabel}
                    muteLabel={messages.muteLabel}
                    unmuteLabel={messages.unmuteLabel}
                    isMuted={isMuted}
                    onBack={handleBack}
                    onClose={closeDialog}
                    onToggleMuted={toggleMuted}
                  />
                </div>
                {preparationMain}
              </div>
            )}
            <footer
              className={`${styles.preparationFooter} ${
                dialogState.request.kind === 'pd' ? styles.preparationFooterWithLegal : ''
              }`}
            >
              <PrimaryButton onClick={continueFromPreparation}>
                {messages.preparation.next}
              </PrimaryButton>
              {dialogState.request.kind === 'pd' ? (
                <LegalNotice messages={messages.method} className={styles.preparationLegalText} />
              ) : null}
            </footer>
          </section>
        ) : null}

        {dialogState.step === 'video' ? (
          <FaceWidthCardVideoGuide
            backLabel={messages.backLabel}
            closeLabel={messages.closeLabel}
            muteLabel={messages.muteLabel}
            unmuteLabel={messages.unmuteLabel}
            replayLabel={messages.preparation.replay}
            startLabel={messages.example.start}
            videoTitle={messages.preparation.videoTitle}
            videoInstructions={messages.preparation.videoInstructions}
            videoCompleted={messages.preparation.videoCompleted}
            videoUrl={preparationVideoUrl}
            isMuted={isMuted}
            isSdkReady={isSdkReady}
            isStartingCamera={isStartingCamera}
            hasCompletedInSession={hasCompletedCardVideoInSessionRef.current}
            onBack={handleBack}
            onClose={closeDialog}
            onComplete={() => {
              hasCompletedCardVideoInSessionRef.current = true;
            }}
            onToggleMuted={toggleMuted}
            onStart={() => void startCamera(dialogState.request, dialogState.config)}
          />
        ) : null}

        {dialogState.step === 'pd-risk' ? (
          <section className={styles.prescriptionRiskOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/pd-preparation-background.png"
              alt=""
              fill
              sizes="100vw"
              className={styles.prescriptionRiskBackground}
              aria-hidden
            />
            <div className={styles.prescriptionRiskScrim} aria-hidden />
            <div
              className={styles.prescriptionRiskDialog}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="prescription-risk-title"
            >
              <h1 id="prescription-risk-title">{messages.prescriptionRisk.title}</h1>
              <p>{messages.prescriptionRisk.description}</p>
              <p>
                {messages.prescriptionRisk.recommendation}{' '}
                <a href={env.NEXT_PUBLIC_PD_RULE_PDF} target="_blank" rel="noreferrer">
                  {messages.prescriptionRisk.pdRuler}
                </a>
                .
              </p>
              <div className={styles.prescriptionRiskActions}>
                <PrimaryButton
                  onClick={() =>
                    setDialogState({
                      step: 'preparation',
                      request: dialogState.request,
                      config: { mode: 'card' },
                    })
                  }
                >
                  {messages.prescriptionRisk.continue}
                </PrimaryButton>
                <button type="button" onClick={closeDialog}>
                  {messages.prescriptionRisk.exit}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'example' ? (
          <section className={styles.contentScreen}>
            <HeaderControls
              backLabel={messages.backLabel}
              closeLabel={messages.closeLabel}
              onBack={handleBack}
              onClose={closeDialog}
            />
            <main className={`${styles.screenBody} ${styles.exampleBody}`}>
              <h1>{messages.example.title}</h1>
              <div className={styles.correctExample}>
                <div className={styles.correctImage}>
                  <Image
                    src={CORRECT_EXAMPLE_IMAGE_BY_MODE[dialogState.config.mode]}
                    alt=""
                    fill
                    sizes="164px"
                  />
                </div>
                <ul>
                  {EXAMPLE_INSTRUCTION_KEYS_BY_MODE[dialogState.config.mode].map(
                    (instructionKey) => (
                      <li key={instructionKey}>
                        <span className={styles.exampleCheck} aria-hidden>
                          <Image
                            src="/zeelool-next/lens-measurement/check-circle.svg"
                            alt=""
                            width={14}
                            height={14}
                          />
                          <Image
                            src="/zeelool-next/lens-measurement/check-mark.svg"
                            alt=""
                            width={8}
                            height={6}
                          />
                        </span>
                        <span>{messages.example[instructionKey]}</span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
              <div className={styles.incorrectGrid}>
                {SUPPORTING_EXAMPLES_BY_MODE[dialogState.config.mode].map(
                  ({ image, messageKey }) => (
                    <figure key={messageKey}>
                      <div className={styles.incorrectImage}>
                        <Image
                          src={`/zeelool-next/lens-measurement/${image}`}
                          alt=""
                          fill
                          sizes="100px"
                        />
                      </div>
                      <figcaption>{messages.example[messageKey]}</figcaption>
                    </figure>
                  ),
                )}
              </div>
            </main>
            <div className={styles.footerButton}>
              <PrimaryButton
                disabled={!isSdkReady || isStartingCamera}
                loading={!isSdkReady || isStartingCamera}
                onClick={() => void startCamera(dialogState.request, dialogState.config)}
              >
                {messages.example.start}
              </PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'permission' ? (
          <section className={styles.permissionOverlay} aria-live="polite">
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.permissionBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.permissionClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.permissionContent}>
              <PermissionCameraIcon status="on" />
              <p>{messages.camera.permission}</p>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'camera' ? (
          <section className={styles.cameraOverlay}>
            <div className={styles.cameraControls}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
            </div>
            <div className={styles.cameraPromptGroup} aria-live="polite">
              {isFinalMeasurement ? (
                <div
                  className={styles.measurementProgress}
                  role="progressbar"
                  aria-label={messages.camera.progress.replace(
                    '{progress}',
                    String(measurementProgress),
                  )}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={measurementProgress}
                >
                  <span
                    className={styles.measurementProgressRing}
                    style={{ '--measurement-progress': `${measurementProgress}%` } as CSSProperties}
                    aria-hidden
                  />
                  <span className={styles.measurementProgressValue} aria-hidden>
                    {measurementProgress}%
                  </span>
                </div>
              ) : promptVisual ? (
                <MeasurementPromptIcon visual={promptVisual} />
              ) : null}
              <p>{isStartingCamera ? messages.camera.starting : prompt}</p>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'result' ? (
          <section className={styles.resultOverlay}>
            <div className={styles.resultSoundControl}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
            </div>
            <div className={styles.resultSheet}>
              <button
                type="button"
                className={styles.resultCloseButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
              <div className={styles.resultPrimaryMetric}>
                <p>{resultPrimary?.label}</p>
                <strong>{resultPrimary?.value}</strong>
              </div>
              {!measurementPersistence.feedbackSubmitted ? (
                <>
                  <div className={styles.feedbackPrompt}>
                    <span aria-hidden />
                    <p>{messages.result.experience}</p>
                    <span aria-hidden />
                  </div>
                  <fieldset className={styles.feedback}>
                    <legend className="sr-only">{messages.result.feedbackLabel}</legend>
                    <button
                      type="button"
                      aria-label={messages.result.dislikeLabel}
                      aria-pressed={feedback === 'dislike'}
                      onClick={() => setFeedback('dislike')}
                    >
                      <Image
                        src={
                          FEEDBACK_ICON_BY_STATE.dislike[
                            feedback === 'dislike' ? 'selected' : 'default'
                          ]
                        }
                        alt=""
                        width={20}
                        height={20}
                        aria-hidden
                        className={styles.dislikeIcon}
                      />
                    </button>
                    <button
                      type="button"
                      aria-label={messages.result.likeLabel}
                      aria-pressed={feedback === 'like'}
                      onClick={() => setFeedback('like')}
                    >
                      <Image
                        src={
                          FEEDBACK_ICON_BY_STATE.like[feedback === 'like' ? 'selected' : 'default']
                        }
                        alt=""
                        width={20}
                        height={20}
                        aria-hidden
                      />
                    </button>
                  </fieldset>
                </>
              ) : null}
            </div>
            <div className={styles.resultActions}>
              <button type="button" disabled={isSavingResult} onClick={remeasureFromResult}>
                {messages.result.remeasure}
              </button>
              <PrimaryButton
                disabled={isSavingResult}
                loading={isSavingResult}
                onClick={saveResult}
              >
                {resultSubmitButtonName}
              </PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' && dialogState.reason === 'permission' ? (
          <section className={styles.permissionFailureOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.permissionFailureBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.permissionFailureClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.permissionFailureContent}>
              <PermissionCameraIcon status="off" />
              <p>{messages.failure.permission}</p>
              <PrimaryButton onClick={refreshPage}>{messages.failure.refresh}</PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' &&
        (dialogState.reason === 'unsupported' || dialogState.reason === 'integration') ? (
          <section className={styles.unsupportedFailureOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.unsupportedFailureBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.unsupportedFailureClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.unsupportedFailureContent} role="alert">
              <MeasurementFailureIcon />
              <p>
                {dialogState.reason === 'unsupported'
                  ? messages.failure.unsupported
                  : messages.failure.unavailable}
              </p>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' && dialogState.reason === 'recoverable' ? (
          <section className={styles.recoverableFailureOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.recoverableFailureBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.recoverableFailureClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.recoverableFailureContent} role="alert">
              <MeasurementFailureIcon />
              <p>{messages.failure.recoverable}</p>
              <PrimaryButton onClick={refreshPage}>{messages.failure.refresh}</PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' && dialogState.reason === 'replace-card' ? (
          <section className={styles.cardFailureOverlay}>
            <div className={styles.cardFailureBackgroundFrame} aria-hidden>
              <Image
                src="/zeelool-next/lens-measurement/runtime/card-failure-background.png"
                alt=""
                fill
                sizes="663px"
                priority
              />
            </div>
            <span className={styles.cardFailureTint} aria-hidden />
            <div className={styles.cameraControls}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
            </div>
            <div className={styles.cardFailureContent} role="alert">
              <CardFailureIcon />
              <p>{messages.failure.replaceCard}</p>
            </div>
            <div className={styles.cardFailureRetry}>
              <PrimaryButton onClick={retryFromFailure}>{messages.failure.retry}</PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' &&
        (dialogState.reason === 'invalid-result' || dialogState.reason === 'timeout') ? (
          <section className={styles.measurementFailureOverlay}>
            <div className={styles.measurementFailureBackgroundFrame} aria-hidden>
              <Image
                src="/zeelool-next/lens-measurement/runtime/card-failure-background.png"
                alt=""
                fill
                sizes="663px"
                priority
              />
            </div>
            <span className={styles.measurementFailureTint} aria-hidden />
            <div className={styles.cameraControls}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
            </div>
            <div className={styles.measurementFailureContent} role="alert">
              <MeasurementFailureIcon />
              <p>{messages.failure.retryMeasurement}</p>
            </div>
            <div className={styles.measurementFailureRetry}>
              <PrimaryButton onClick={retryFromFailure}>{messages.failure.retry}</PrimaryButton>
            </div>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
